import React, {useEffect, useState} from "react";
import axios from "axios";
import ReportTable from "../common/ReportTable";
import TableSkeleton from "../../utils/TableSkeleton";
import EmptyTableState from "../../utils/EmptyTableState";
import * as Constants from "../../utils/Constants";
import {customStyles} from "../../utils/DataTableCustomStyle";
import ReportAssetFilter from "../ReportAssetFilter";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileExcel, faFilePdf} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import "../../styles/components/reports.css";

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const parseTs = (v) => {
  if (!v) return null;
  if (typeof v === "number") return new Date(v * 1000);
  const normalized = String(v).replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
  return new Date(normalized);
};

const Gt06FleetDistance = () => {
  const [assetList, setAssetList] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const TODAY = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(TODAY);
  const [endDate, setEndDate] = useState(TODAY);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(
          `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_GET_ALL}`
        );
        const all = res.data?.responseData || [];
        setAssetList(all.filter((a) => (a.assetType || "").toUpperCase() === "GT06_TRACKER"));
      } catch (err) {
        console.error("Error fetching GT06 assets:", err);
      }
    })();
  }, []);

  const onSearch = async () => {
    const ids = Object.keys(checkedItems).filter((k) => checkedItems[k]);
    if (!startDate || !endDate) {
      showToast("Please select a date range.", {type: "error"});
      return;
    }
    setIsLoading(true);
    try {
      const params = new URLSearchParams({startDate, endDate, timezone: TZ});
      if (ids.length) params.set("assetIds", ids.join(","));
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(
        `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/gt06-reports/fleet-distance?${params}`
      );
      const enriched = (res.data || []).map((r) => ({
        ...r,
        assetName: assetList.find((a) => a.assetUnitId === r.assetUnitId)?.name || r.assetUnitId,
      }));
      setRows(enriched);
    } catch (err) {
      console.error("Fleet distance failed:", err);
      showToast(
        err?.response?.data?.responseMessage || "Failed to load fleet distance",
        {type: "error"}
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (id) => setCheckedItems((p) => ({...p, [id]: !p[id]}));

  const handleClear = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setCheckedItems({});
    setRows([]);
  };

  const fmtDt = (v) => {
    const d = parseTs(v);
    return d && !isNaN(d) ? d.toLocaleString() : "—";
  };
  const num = (v, suffix = "") => (v == null ? "—" : `${v}${suffix}`);

  // Summary cards across the top
  const totalKm = rows.reduce((s, r) => s + (r.distanceKm || 0), 0);
  const totalEngineHrs = rows.reduce((s, r) => s + (r.engineHours || 0), 0);
  const totalTrips = rows.reduce((s, r) => s + (r.tripCount || 0), 0);
  const totalAlerts = rows.reduce((s, r) => s + (r.totalAlerts || 0), 0);

  const columns = [
    {name: "Asset", id: "Asset", selector: (r) => r.assetName, sortable: true, grow: 2, minWidth: "130px"},
    {name: "Distance (km)", id: "Distance (km)", selector: (r) => r.distanceKm, sortable: true, grow: 1.2, minWidth: "110px", cell: (r) => num(r.distanceKm)},
    {name: "Engine Hrs", id: "Engine Hrs", selector: (r) => r.engineHours, sortable: true, grow: 1.1, minWidth: "110px", cell: (r) => num(r.engineHours)},
    {name: "Trips", id: "Trips", selector: (r) => r.tripCount, sortable: true, grow: 0.6, minWidth: "70px"},
    {name: "Alerts", id: "Alerts", selector: (r) => r.totalAlerts, sortable: true, grow: 0.9, minWidth: "90px"},
  ];

  const ExpandedRow = ({data: r}) => {
    const fields = [
      ["IMEI", r.assetUnitId],
      ["Moving", `${Math.round(r.movingMinutes ?? 0)} min`],
      ["Idle", `${Math.round(r.idleMinutes ?? 0)} min`],
      ["Max Speed", r.maxSpeedKmph != null ? `${r.maxSpeedKmph} km/h` : "—"],
      ["First Seen", fmtDt(r.firstSeen)],
      ["Last Seen", fmtDt(r.lastSeen)],
    ];
    return (
      <div style={{display: "flex", flexWrap: "wrap", padding: "10px 20px 12px", background: "#fafbff", borderBottom: "2px solid #e9ecef", borderLeft: "4px solid #FF683A"}}>
        {fields.map(([label, value], i, arr) => (
          <div key={label} style={{display: "flex", flexDirection: "column", gap: "3px", padding: "2px 20px 2px 0", marginRight: "20px", borderRight: i < arr.length - 1 ? "1px solid #e5e7eb" : "none"}}>
            <span style={{fontSize: "0.62rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em"}}>{label}</span>
            <span style={{fontSize: "0.84rem", fontWeight: 500, color: "#1f2937"}}>{value}</span>
          </div>
        ))}
      </div>
    );
  };

  const exportExcel = () => {
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(
      rows.map((r) => ({
        Asset: r.assetName,
        IMEI: r.assetUnitId,
        DistanceKm: r.distanceKm,
        EngineHours: r.engineHours,
        MovingMinutes: r.movingMinutes,
        IdleMinutes: r.idleMinutes,
        Trips: r.tripCount,
        MaxKmph: r.maxSpeedKmph,
        Alerts: r.totalAlerts,
        FirstSeen: r.firstSeen,
        LastSeen: r.lastSeen,
      }))
    );
    const wb = {Sheets: {data: ws}, SheetNames: ["FleetDistance"]};
    const buf = XLSX.write(wb, {bookType: "xlsx", type: "array"});
    FileSaver.saveAs(new Blob([buf]), `gt06_fleet_distance_${Date.now()}.xlsx`);
  };

  const exportPdf = () => {
    if (!rows.length) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.text(`GT06 Fleet Distance Summary — ${startDate} to ${endDate}`, 30, 30);
    doc.setFontSize(9);
    doc.text(
      `Total: ${totalKm.toFixed(1)} km · ${totalEngineHrs.toFixed(1)} engine hrs · ${totalTrips} trips · ${totalAlerts} alerts`,
      30, 46
    );
    autoTable(doc, {
      startY: 60,
      head: [["Asset", "IMEI", "Km", "EngHrs", "Move(m)", "Idle(m)", "Trips", "Max km/h", "Alerts"]],
      body: rows.map((r) => [
        r.assetName,
        r.assetUnitId,
        r.distanceKm,
        r.engineHours,
        Math.round(r.movingMinutes ?? 0),
        Math.round(r.idleMinutes ?? 0),
        r.tripCount,
        r.maxSpeedKmph,
        r.totalAlerts,
      ]),
      styles: {fontSize: 8},
    });
    doc.save(`gt06_fleet_distance_${Date.now()}.pdf`);
  };

  return (
    <div className="report-content-split">
      <div className="report-main-area">
        <div className="report-header-actions">
          <span className="report-results-title">
            Fleet Distance Summary {rows.length > 0 && `(${rows.length})`}
          </span>
          {rows.length > 0 && (
            <div className="export-buttons">
              <button className="export-btn" onClick={exportExcel}>
                <FontAwesomeIcon icon={faFileExcel} /> Excel
              </button>
              <button className="export-btn" onClick={exportPdf}>
                <FontAwesomeIcon icon={faFilePdf} /> PDF
              </button>
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="fleet-summary-cards">
            <div className="fleet-summary-card">
              <div className="fleet-summary-value">{totalKm.toFixed(1)}</div>
              <div className="fleet-summary-label">Total km</div>
            </div>
            <div className="fleet-summary-card">
              <div className="fleet-summary-value">{totalEngineHrs.toFixed(1)}</div>
              <div className="fleet-summary-label">Engine hrs</div>
            </div>
            <div className="fleet-summary-card">
              <div className="fleet-summary-value">{totalTrips}</div>
              <div className="fleet-summary-label">Trips</div>
            </div>
            <div className="fleet-summary-card">
              <div className="fleet-summary-value">{totalAlerts}</div>
              <div className="fleet-summary-label">Alerts</div>
            </div>
          </div>
        )}

        <div className="report-table-wrapper">
          <ReportTable
            columns={columns}
            data={rows}
            customStyles={customStyles}
            progressPending={isLoading}
            progressComponent={<TableSkeleton />}
            fixedHeader
            fixedHeaderScrollHeight="100%"
            defaultSortFieldId={3}
            defaultSortAsc={false}
            noDataComponent={<EmptyTableState variant="search" />}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            expandableRows
            expandableRowExpanded={() => true}
            expandableRowsComponent={ExpandedRow}
          />
        </div>
      </div>

      <div className="report-filter-sidebar">
        <div className="sidebar-header">
          <h3 className="sidebar-title">Search Filters</h3>
        </div>
        <div className="sidebar-content">
          <div className="filter-group">
            <label className="filter-label">Select GPS Trackers (leave empty for all)</label>
            <div className="asset-list-wrapper">
              <ReportAssetFilter assets={assetList} checkedItems={checkedItems} setCheckedItems={setCheckedItems} />
            </div>
          </div>
          <div>
            <label className="filter-label">Start Date</label>
            <input
              type="date"
              className="filter-input"
              value={startDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="filter-label">End Date</label>
            <input
              type="date"
              className="filter-input"
              value={endDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div className="sidebar-footer">
          <div className="filter-actions">
            <button className="btn-report" onClick={onSearch}>Search</button>
            <button className="btn-report btn-secondary" onClick={handleClear}>Clear</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gt06FleetDistance;
